import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheckIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { AuditLogFilters, useAuditLog } from '@/services/api';
import useMockDataToast from '@/hooks/useMockDataToast';
import useDebounce from '@/hooks/useDebounce';

const columnLabels = ['時間戳記', '操作人員', '角色', '動作', '資源', '詳細'] as const;

const AuditLog: React.FC = () => {
  const [queryInput, setQueryInput] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [actionInput, setActionInput] = useState('all');
  const [appliedFilters, setAppliedFilters] = useState<AuditLogFilters>({});
  const [formError, setFormError] = useState<string | null>(null);
  const debouncedQuery = useDebounce(queryInput, 450);
  const {
    data: entries = [],
    isLoading,
    isFetching,
    error,
    isFallback,
    fallbackMessage,
  } = useAuditLog(true, appliedFilters);
  useMockDataToast(isFallback, '稽核服務暫時無法使用，顯示快取時間軸。', fallbackMessage);
  const isRefreshing = !isLoading && isFetching;

  const actions = useMemo(() => {
    const unique = Array.from(new Set(entries.map((entry) => entry.action))).filter(Boolean);
    unique.sort();
    return unique;
  }, [entries]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (startDateInput && endDateInput && startDateInput > endDateInput) {
      setFormError('結束日期需在開始日期之後');
      return;
    }
    setFormError(null);
    setAppliedFilters({
      query: queryInput.trim() || undefined,
      startDate: startDateInput || undefined,
      endDate: endDateInput || undefined,
      action: actionInput !== 'all' ? actionInput : undefined,
    });
  };

  const handleReset = () => {
    setQueryInput('');
    setStartDateInput('');
    setEndDateInput('');
    setActionInput('all');
    setFormError(null);
    setAppliedFilters({});
  };

  useEffect(() => {
    const normalized = debouncedQuery.trim();
    const queryValue = normalized ? normalized : undefined;
    setAppliedFilters((prev) => {
      if (prev.query === queryValue) {
        return prev;
      }
      return { ...prev, query: queryValue };
    });
  }, [debouncedQuery]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="section-title">合規紀錄</p>
          <h1 className="text-2xl font-semibold text-slate-900">稽核日誌</h1>
          <p className="text-sm text-slate-500">
            所有敏感操作皆會記錄，以維持醫院合規與客戶信任。
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
          <div className="flex items-center gap-2 font-semibold text-slate-900">
            <ShieldCheckIcon className="h-5 w-5 text-brand-blue" />
            防竄改
          </div>
          <p className="text-xs text-slate-500">同步到 Cloudflare D1 與 Logpush</p>
        </div>
      </div>

      <section className="glass-panel space-y-4 p-6">
        <form className="grid gap-4 md:grid-cols-4" onSubmit={handleSubmit}>
          <label className="text-xs font-semibold text-slate-500">
            關鍵字
            <input
              type="search"
              value={queryInput}
              onChange={(event) => setQueryInput(event.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
              placeholder="搜尋人員、動作、資源"
            />
          </label>
          <label className="text-xs font-semibold text-slate-500">
            開始日期
            <input
              type="date"
              value={startDateInput}
              onChange={(event) => setStartDateInput(event.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
            />
          </label>
          <label className="text-xs font-semibold text-slate-500">
            結束日期
            <input
              type="date"
              value={endDateInput}
              onChange={(event) => setEndDateInput(event.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
            />
          </label>
          <label className="text-xs font-semibold text-slate-500">
            動作
            <select
              value={actionInput}
              onChange={(event) => setActionInput(event.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/20"
            >
              <option value="all">全部</option>
              {actions.map((action) => (
                <option key={action} value={action}>
                  {action}
                </option>
              ))}
            </select>
          </label>
          {formError && (
            <p className="md:col-span-4 text-xs font-semibold text-rose-600" role="alert">
              {formError}
            </p>
          )}
          <div className="md:col-span-4 flex items-center gap-3">
            <button
              type="submit"
              className="flex items-center gap-2 rounded-xl bg-brand-blue px-4 py-2 text-xs font-semibold text-white shadow-soft"
            >
              <FunnelIcon className="h-4 w-4" /> 套用篩選
            </button>
            <button
              type="button"
              className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600"
              onClick={handleReset}
            >
              清除
            </button>
            <p className="text-xs text-slate-500">
              目前顯示 {entries.length} 筆紀錄
              {isRefreshing && <span className="ml-2 text-brand-blue">更新中…</span>}
            </p>
          </div>
        </form>
      </section>

      {error && (
        <div className="glass-panel border border-rose-200 bg-rose-50 p-6 text-rose-700">
          <p className="text-sm font-semibold">無法載入稽核記錄。</p>
          <p className="text-xs mt-1">
            {error instanceof Error ? error.message : '請稍後再試。'}
          </p>
        </div>
      )}

      <section className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-auto divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                {columnLabels.map((label) => (
                  <th key={label} className="px-5 py-3">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={columnLabels.length} className="px-5 py-10 text-center text-slate-400">
                    同步稽核紀錄…
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3 text-xs text-slate-500">
                      {new Date(entry.timestamp).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 font-semibold text-slate-900">{entry.actor}</td>
                    <td className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {entry.role}
                    </td>
                    <td className="px-5 py-3 text-slate-700">{entry.action}</td>
                    <td className="px-5 py-3 text-slate-500">{entry.resource}</td>
                    <td className="px-5 py-3 text-slate-700">{entry.detail ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AuditLog;
